import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PCContainer from "../../components/atoms/layout/pcContainer";
import SBannerSwiper from "../../components/templates/study/sBannerSwiper";
import LSRgDisplay02 from "../../components/molecules/text/linSeedKr/Rg/lsRgDisplay02";
import LectureItemPage from "../../components/organisms/lecture/lectureItemPage";
import Empty from "../../components/molecules/empty";
import Pagination from "../../components/molecules/pagination";
import LSRgH2HeadLine from "../../components/molecules/text/linSeedKr/Rg/lsRgH2HeadLine";
import SelecteBoxValue from "../../components/organisms/selectbox/selecteBoxValue";

import { errorHandler } from "../../util/errorHandler";
import LectureApi from "../../api/lecture.api";
import useDisplay from "../../hooks/useDisplay";
import { route } from "../../router/route";
import BannerApi from "../../api/banner.api";
import StyledCategoryItem from "../../components/molecules/button/styledCategoryItem";
import Loading from "../../components/templates/loading";

const StudyList = () => {
  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars

  const [bannerList, setBannerList] = useState([]);

  const [categoryBoxOpen, setCategoryBoxOpen] = useState(false);
  const [categoryValue, setCategoryValue] = useState(-1);
  const [categoryList, setCategoryList] = useState([
    { title: "전체", value: -1 },
  ]);

  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const page = useMemo(() => {
    const pageParam = Number(searchParams.get("page"));
    return Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 9, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  useEffect(() => {
    categoryListFunc();
    bannerListFunc();
  }, []);

  useEffect(() => {
    const requestedCategory = Number(searchParams.get("categoryValue"));
    setCategoryValue(
      Number.isInteger(requestedCategory) && requestedCategory > 0
        ? requestedCategory
        : -1,
    );

    dataListFunc();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const bannerListFunc = async () => {
    try {
      const response = (
        await BannerApi.getInfo({
          page: 1,
          size: 0,
          bannerType: "STUDY",
        })
      ).data.data.content;

      setBannerList(response ?? []);
    } catch (error) {
      errorHandler(error);
    }
  };

  const categoryListFunc = async () => {
    try {
      const studyCList = (
        await LectureApi.getCategoryList({
          page: 1,
          size: 0,
          lectureCategoryType: "FINANCE_STUDY",
          activation: "Active",
        })
      ).data.data.content;

      const categoryTemp = (studyCList ?? []).map((item) => {
        return {
          ...item,
          value: item.id,
        };
      });

      setCategoryList([{ title: "전체", value: -1 }, ...categoryTemp]);
    } catch (error) {
      errorHandler(error);
    }
  };

  const dataListFunc = async () => {
    setIsLoading(true);
    try {
      const data = {
        page: page,
        size: pagination.size,
        lectureCategoryType: "FINANCE_STUDY",
        activation: "Active",
      };

      const requestedCategory = Number(searchParams.get("categoryValue"));
      if (Number.isInteger(requestedCategory) && requestedCategory > 0) {
        data.lectureCategoryId = requestedCategory;
      }

      const response = (await LectureApi.getList(data)).data.data;

      setProductList(response.content ?? []);
      setPagination((current) => ({
        ...current,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / pagination.size),
      }));
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <StudyListSection>
        {bannerList.length > 0 ? (
          <SBannerSwiper bannerList={bannerList} />
        ) : null}

        <PCContainer
          style={{
            flexDirection: "column",
            padding: isMobile
              ? "40px 20px 80px 20px"
              : isTablet
                ? "40px 60px 80px 60px"
                : "80px 0px 150px 0px",
            width: "100%",
          }}
        >
          <LSRgDisplay02
            style={{
              color: "#000000",
              marginBottom: isMobile ? 16 : 30,
              fontSize: isMobile ? 16 : 24,
              lineHeight: isMobile ? "22.4px" : "33.6px",
            }}
          >
            🔎 카테고리별로 무료 스터디를 찾아보세요
          </LSRgDisplay02>

          {isMobile ? (
            <SelecteBoxValue
              style={{
                marginBottom: 20,
              }}
              selectedValue={categoryValue}
              isOpen={categoryBoxOpen}
              wrapperHandler={() => {
                setCategoryBoxOpen(!categoryBoxOpen);
              }}
              itemList={categoryList}
              selectedValueSetterFunc={(value) => {
                setCategoryValue(value);
                navigate(`${route.study_list}?page=1&categoryValue=${value}`);
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: "16px",
                marginBottom: 60,
              }}
            >
              {categoryList.map((item) => {
                return (
                  <StyledCategoryItem
                    key={item.value}
                    onClick={() => {
                      navigate(
                        `${route.study_list}?page=1&categoryValue=${item.value}`,
                      );
                    }}
                    isActive={categoryValue === item.value}
                    title={item.title}
                  />
                );
              })}
            </div>
          )}

          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: isMobile ? "19px 0px" : "30px",
                flexDirection: "row",
                justifyContent: isSmall ? "space-between" : "flex-start",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              {isLoading ? (
                <Loading />
              ) : productList.length > 0 ? (
                productList.map((item) => {
                  return <LectureItemPage key={item.id} data={item} />;
                })
              ) : (
                <Empty title={"등록된 강의가 없습니다."} />
              )}
            </div>
          </div>

          {productList.length > 0 ? (
            <Pagination
              route={route.study_list}
              queryString={`categoryValue=${categoryValue}`}
              currentPage={Number(page)}
              totalPages={pagination.totalPages}
              rangeSize={pagination.rangeSize}
            />
          ) : null}
        </PCContainer>
      </StudyListSection>
    </>
  );
};

export default StudyList;

const StudyListSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;
